// Botões de ação da ficha (só os que a pessoa pode usar) e os respetivos diálogos.
import { useApi, useMe } from "@ecliptica/api-client/react";
import { assignableRoles, canChangeRoleOf, canSanction, compensationValue } from "@ecliptica/contexts-accounts";
import { useTranslation } from "@ecliptica/i18n";
import { Alert, Button, Input, Label, NativeSelect } from "@ecliptica/ui";
import { useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Coins, Gavel, KeyRound, LogOut, ShieldOff, UserCog } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useCan } from "../../features/auth/RequireStaff";
import { ROLES_KEY, useRoleLabel, useRoles } from "../../features/roles/useRoles";
import { ActionDialog, type ApiResult } from "../../features/users/ActionDialog";

type Kind = "ban" | "unban" | "revoke" | "reset" | "verify" | "compensate" | "role";
const DURATIONS = ["1", "3", "7", "30", "forever"] as const;

export function UserActions({ user }: { user: { id: string; role: string; banned: boolean; emailVerified: boolean } }) {
  const { t } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const can = useCan();
  const me = useMe();
  const [params] = useSearchParams();
  const [open, setOpen] = useState<Kind | null>(params.get("act") === "compensate" ? "compensate" : null);
  const [duration, setDuration] = useState<(typeof DURATIONS)[number]>("3");
  const [comp, setComp] = useState({ coins: 0, dust: 0, packs: 0 });
  const [role, setRole] = useState<string>(user.role);
  const roles = useRoles();
  const roleLabel = useRoleLabel();
  const param = { param: { id: user.id } };
  const refresh = async <R extends ApiResult>(res: R) => {
    if (res.ok) {
      await qc.invalidateQueries({ queryKey: ["user", user.id] });
      await qc.invalidateQueries({ queryKey: ["users"] });
      await qc.invalidateQueries({ queryKey: ["compensations"] });
      await qc.invalidateQueries({ queryKey: ROLES_KEY });
    }
    return res;
  };
  const limit = me.data?.compensationLimit ?? null;

  // Mudar papel: mesmas regras do backend (hierarquia, nunca o próprio, nunca o último admin).
  const actor = { id: me.data?.user.id ?? "", role: me.data?.user.role ?? "player" };
  const roleSet = roles.data?.set;
  const roleCheck = roleSet ? canChangeRoleOf(roleSet, actor, user, roles.data!.adminCount) : null;
  const options = roleSet ? assignableRoles(roleSet, actor) : [];
  // Sanções: só a contas abaixo do próprio papel (admin: qualquer outra). Sem papéis carregados, o backend decide.
  const sanction = can("users.sanction") && (!roleSet || canSanction(roleSet, actor, user).ok);

  const buttons: { kind: Kind; show: boolean; icon: typeof Gavel; label: string; destructive?: boolean }[] = [
    { kind: "ban", show: sanction && !user.banned, icon: Gavel, label: t("users.actions.ban"), destructive: true },
    { kind: "unban", show: sanction && user.banned, icon: ShieldOff, label: t("users.actions.unban") },
    { kind: "revoke", show: sanction, icon: LogOut, label: t("users.actions.revoke") },
    { kind: "reset", show: can("users.reset"), icon: KeyRound, label: t("users.actions.reset") },
    { kind: "verify", show: can("users.reset") && !user.emailVerified, icon: BadgeCheck, label: t("users.actions.verify") },
    { kind: "compensate", show: can("users.compensate"), icon: Coins, label: t("users.actions.compensate") },
    { kind: "role", show: can("users.roles"), icon: UserCog, label: t("users.actions.role") },
  ];

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {buttons
          .filter((b) => b.show)
          .map(({ kind, icon: Icon, label, destructive }) => (
            <Button key={kind} size="sm" variant={destructive ? "destructive" : "outline"} onClick={() => setOpen(kind)}>
              <Icon /> {label}
            </Button>
          ))}
      </div>

      <ActionDialog
        open={open === "ban"}
        onOpenChange={(o) => !o && setOpen(null)}
        title={t("users.actions.ban")}
        hint={t("users.actions.banHint")}
        destructive
        onConfirm={async (reason) => {
          const until = duration === "forever" ? null : new Date(Date.now() + Number(duration) * 86_400_000).toISOString();
          return refresh(await api.v1.admin.users[":id"].ban.$post({ ...param, json: { reason, until } }));
        }}
      >
        <label className="flex flex-col gap-1.5 text-sm font-semibold">
          {t("users.actions.duration")}
          <NativeSelect value={duration} onChange={(e) => setDuration(e.target.value as (typeof DURATIONS)[number])}>
            {DURATIONS.map((d) => (
              <option key={d} value={d}>
                {t(`users.actions.dur.${d}`)}
              </option>
            ))}
          </NativeSelect>
        </label>
      </ActionDialog>

      <ActionDialog
        open={open === "unban"}
        onOpenChange={(o) => !o && setOpen(null)}
        title={t("users.actions.unban")}
        onConfirm={async (reason) => refresh(await api.v1.admin.users[":id"].unban.$post({ ...param, json: { reason } }))}
      />
      <ActionDialog
        open={open === "revoke"}
        onOpenChange={(o) => !o && setOpen(null)}
        title={t("users.actions.revoke")}
        hint={t("users.actions.revokeHint")}
        onConfirm={async (reason) => refresh(await api.v1.admin.users[":id"].sessions.revoke.$post({ ...param, json: { reason } }))}
      />
      <ActionDialog
        open={open === "reset"}
        onOpenChange={(o) => !o && setOpen(null)}
        title={t("users.actions.reset")}
        hint={t("users.actions.resetHint")}
        onConfirm={async (reason) => refresh(await api.v1.admin.users[":id"]["password-reset"].$post({ ...param, json: { reason } }))}
      />
      <ActionDialog
        open={open === "verify"}
        onOpenChange={(o) => !o && setOpen(null)}
        title={t("users.actions.verify")}
        onConfirm={async (reason) => refresh(await api.v1.admin.users[":id"]["verify-email"].$post({ ...param, json: { reason } }))}
      />

      <ActionDialog
        open={open === "compensate"}
        onOpenChange={(o) => !o && setOpen(null)}
        title={t("users.actions.compensate")}
        hint={t("users.actions.compHint", { limit: limit ?? "∞" })}
        canSubmit={comp.coins + comp.dust + comp.packs > 0}
        onConfirm={async (reason) => {
          const res = await refresh(await api.v1.admin.users[":id"].compensations.$post({ ...param, json: { ...comp, reason } }));
          if (res.ok) setComp({ coins: 0, dust: 0, packs: 0 });
          return res;
        }}
      >
        <div className="grid grid-cols-3 gap-3">
          {(["coins", "dust", "packs"] as const).map((k) => (
            <div key={k} className="flex flex-col gap-1.5">
              <Label htmlFor={`comp-${k}`}>{t(`users.comp.${k}`)}</Label>
              <Input
                id={`comp-${k}`}
                type="number"
                min={0}
                max={1_000_000}
                value={comp[k]}
                onChange={(e) => setComp((c) => ({ ...c, [k]: Math.max(0, Math.floor(Number(e.target.value) || 0)) }))}
              />
            </div>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">{t("users.actions.compValue", { value: compensationValue(comp) })}</p>
      </ActionDialog>

      <ActionDialog
        open={open === "role"}
        onOpenChange={(o) => !o && setOpen(null)}
        title={t("users.actions.role")}
        hint={t("users.actions.roleHint")}
        askReason={!!roleCheck?.ok}
        canSubmit={!!roleCheck?.ok && role !== user.role && options.some((o) => o.key === role)}
        onConfirm={async (reason) => refresh(await api.v1.admin.users[":id"].role.$put({ ...param, json: { role, reason } }))}
      >
        {roleCheck && !roleCheck.ok ? (
          <Alert variant="warning">{t(`users.actions.errors.${roleCheck.reason}`)}</Alert>
        ) : (
          <label className="flex flex-col gap-1.5 text-sm font-semibold">
            {t("users.actions.newRole")}
            <NativeSelect value={role} onChange={(e) => setRole(e.target.value)}>
              {/* O papel atual aparece sempre (para se ver de onde se parte), mesmo que não se possa dar. */}
              {!options.some((o) => o.key === user.role) && (
                <option value={user.role} disabled>
                  {roleLabel(user.role)}
                </option>
              )}
              {options.map((r) => (
                <option key={r.key} value={r.key}>
                  {roleLabel(r.key, r.labels)}
                </option>
              ))}
            </NativeSelect>
          </label>
        )}
      </ActionDialog>
    </>
  );
}
